import {
  Component, OnInit, OnDestroy, AfterViewInit,
  ElementRef, ViewChild, NgZone, Input
} from '@angular/core';
// (ElementRef já injetado no construtor para medir o scroll)
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

interface BlockData {
  base: THREE.Vector3;
  normal: THREE.Vector3;
  phase: number;
  hover: boolean;
  offset: number;
  scaleV: number;
  baseScale?: THREE.Vector3;
  baseQuat?: THREE.Quaternion; // orientação de repouso (p/ o wobble da levitação)
}

interface Product {
  n: string;
  title: string;
  desc: string;
  fragment: string;
}

@Component({
  selector: 'app-igloo-hero',
  templateUrl: './igloo-hero.component.html',
  styleUrls: ['./igloo-hero.component.scss']
})
export class IglooHeroComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() mode: 'full' | 'landscape' = 'full'; // 'landscape' = só paisagem, sem iglu/pinguins/scroll
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('content', { static: false }) contentRef!: ElementRef<HTMLElement>;
  @ViewChild('blizzard', { static: false }) blizzardRef!: ElementRef<HTMLElement>;
  @ViewChild('scrollHint', { static: false }) scrollHintRef!: ElementRef<HTMLElement>;
  @ViewChild('cards', { static: false }) cardsRef!: ElementRef<HTMLElement>;
  @ViewChild('cardsTrack', { static: false }) cardsTrackRef!: ElementRef<HTMLElement>;
  @ViewChild('cta', { static: false }) ctaRef!: ElementRef<HTMLElement>;
  @ViewChild('brand', { static: false }) brandRef!: ElementRef<HTMLElement>;

  year = new Date().getFullYear(); // ano do copyright no canto do hero

  // Produtos exibidos na fase final do scroll (sobre a paisagem fixa)
  products: Product[] = [
    { n: '01', title: 'Apps', desc: 'Aplicativos sob medida, rápidos e prontos pra escalar.', fragment: 'apps' },
    { n: '02', title: 'Sites', desc: 'Sites empresariais que passam credibilidade e convertem.', fragment: 'sites-empresariais' },
    { n: '03', title: 'Landing Pages', desc: 'Páginas de alta conversão para as suas campanhas.', fragment: 'landing-pages' },
    { n: '04', title: 'Lojas Virtuais', desc: 'E-commerces completos, prontos pra vender.', fragment: 'lojas-virtuais' },
    { n: '05', title: 'Design & Identidade', desc: 'Marca e visual que destacam o seu negócio.', fragment: 'design-identidade' }
  ];

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private igloo!: THREE.Group;
  private snow!: THREE.Group;
  private auroraMat?: THREE.ShaderMaterial; // aurora boreal no céu (uTime animado no loop)
  private starMat?: THREE.ShaderMaterial;   // estrelas cintilantes no céu (uTime animado no loop)
  private blocks: THREE.Mesh[] = [];
  private floatBlocks: THREE.Mesh[] = [];
  private hitProxies: THREE.Mesh[] = []; // colisores fixos p/ o hover (não se movem)
  private snowMap!: THREE.Texture;
  private snowNormal!: THREE.Texture;
  private snowRough!: THREE.Texture;
  private snowAO!: THREE.Texture;
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2(-10, -10); // NDC do mouse (fora da tela por padrão)
  private mouseX = 0;
  private mouseY = 0;
  private slidePenguin: THREE.Group | null = null; // pinguim deslizando na frente do iglu
  private slideBaseY = 0;                            // altura da base do pinguim na neve
  private slideStart = 0;                            // instante (t) em que o deslize começa
  private slideMixer: THREE.AnimationMixer | null = null; // toca a animação de asas/corpo do pinguim
  // Pinguins fixos com movimento idle (respiração + balanço sutil)
  private idlePenguins: { obj: THREE.Object3D; baseY: number; baseScale: number; phase: number }[] = [];
  private frameId = 0;
  private t = 0;
  private lastTime = 0;
  private scrollProgress = 0; // 0 = topo, 1 = totalmente "dentro" do iglu
  // Fator de enquadramento responsivo: afasta a câmera em telas estreitas (retrato/mobile)
  // p/ o iglu caber quase inteiro no primeiro hero. 1 = desktop; cresce até ~2 no celular.
  private camFit = 1;
  // Alvo do olhar suavizado (lookAt fluido, independente de FPS)
  private lookSmooth = new THREE.Vector3(0, 2.8, 0);
  // Ponto de hover suavizado + estado (evita tremor dos blocos na borda do raio)
  private hoverPt = new THREE.Vector3();
  private hoverActive = false;
  // Luz branca interna do iglu (acende no hover, ilumina os blocos por dentro)
  private backLight!: THREE.PointLight;
  // Intro de abertura: a câmera "cai" de uma vista aérea até o enquadramento atual (uma vez)
  private introActive = true;
  private introT = 0;
  private introDur = 2.8; // duração da queda (s)

  constructor(private zone: NgZone, private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.introActive = this.mode === 'full'; // intro de abertura só no hero principal
    this.initScene();
    if (this.mode === 'full') {
      this.buildIgloo();
      this.tryLoadModel();
      this.loadPenguin();
      this.loadPenguin2();
      this.loadSlidePenguin();
    }
    // Roda fora da zona do Angular pra não disparar detecção de mudança a cada frame
    this.zone.runOutsideAngular(() => {
      this.lastTime = performance.now();
      this.animate();
    });

    window.addEventListener('resize', this.onResize);
    if (this.mode === 'full') {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frameId);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    const canvas = this.canvasRef.nativeElement;
    canvas.removeEventListener('pointermove', this.onPointerMove);
    canvas.removeEventListener('pointerleave', this.onPointerLeave);
    this.scene?.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) { mesh.geometry.dispose(); }
      const mat = mesh.material as THREE.Material | THREE.Material[];
      if (Array.isArray(mat)) { mat.forEach((m) => m.dispose()); }
      else if (mat) { mat.dispose(); }
    });
    this.renderer?.dispose();
  }

  /**
   * Enquadramento responsivo: em telas estreitas (retrato/celular) o iglu — que é largo —
   * sai do campo de visão da câmera (FOV vertical de 40°). Calcula um fator que afasta a
   * câmera (modo full) ou abre o FOV (modo paisagem) p/ caber quase inteiro.
   */
  private updateCamFit(w: number, h: number): void {
    const aspect = w / (h || 1);
    // Acima de ~1.5 (paisagem/desktop) não precisa ajuste; abaixo, afasta proporcionalmente
    // (até 3.2x no retrato do celular) p/ caber o iglu inteiro + os pinguins ao redor.
    this.camFit = aspect >= 1.5 ? 1 : THREE.MathUtils.clamp(1.5 / aspect, 1, 3.2);
    if (this.mode === 'landscape' && this.camera) {
      // Sem loop de scroll: abre o FOV p/ não cortar a paisagem em telas estreitas
      // (limitado a 58° p/ evitar distorção de grande-angular).
      this.camera.fov = Math.min(58, 40 * this.camFit);
      this.camera.updateProjectionMatrix();
    }
  }

  // ---------- Cena ----------
  private initScene(): void {
    const canvas = this.canvasRef.nativeElement;
    const { clientWidth: w, clientHeight: h } = canvas.parentElement!;
    this.updateCamFit(w, h);

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(0x2c3548, 16, 175); // névoa azul-noite

    // Céu desenhado no canvas: escuro no topo (= header) clareando até a neve.
    // Assim o body não aparece e não há "linha" clara abaixo do header.
    const skyCanvas = document.createElement('canvas');
    skyCanvas.width = 4;
    skyCanvas.height = 512;
    const sctx = skyCanvas.getContext('2d')!;
    const grd = sctx.createLinearGradient(0, 0, 0, 512);
    // Anoitecer: azul-noite escuro no alto, clareando só de leve até o horizonte
    grd.addColorStop(0, '#05070d');    // topo (header) — quase preto
    grd.addColorStop(0.22, '#0a1020'); // azul-noite profundo
    grd.addColorStop(0.5, '#16223c');  // azul-noite
    grd.addColorStop(0.8, '#2c3a54');  // clareia de leve
    grd.addColorStop(1, '#4c5a6e');    // horizonte (azul acinzentado, baixo)
    sctx.fillStyle = grd;
    sctx.fillRect(0, 0, 4, 512);
    this.scene.background = new THREE.CanvasTexture(skyCanvas);

    // FOV responsivo: paisagem abre o ângulo em telas estreitas; full usa 40° fixo
    // (o enquadramento do full é ajustado afastando a câmera no loop, via camFit).
    const fov = this.mode === 'landscape' ? 40 * this.camFit : 40;
    this.camera = new THREE.PerspectiveCamera(fov, w / h, 0.1, 500);
    if (this.mode === 'landscape') {
      this.camera.position.set(0, 6, 18);   // vista ampla da paisagem (sem iglu)
      this.camera.lookAt(0, 5, -40);
    } else {
      this.camera.position.set(-11, 2.6, 12); // câmera baixa: vê o iglu de baixo
      this.camera.lookAt(0, 2.8, 0);
    }

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.38; // noite caindo (ambiente escuro)

    this.loadSnowTextures();

    // Noite: luz ambiente baixa e fria + "luar" como key (azulado, suave)
    const hemi = new THREE.HemisphereLight(0x6d7a92, 0x3a4150, 0.28);
    this.scene.add(hemi);

    const ambient = new THREE.AmbientLight(0x586478, 0.1);
    this.scene.add(ambient);

    // Luar: luz fria azulada, ainda razoavelmente alta
    const key = new THREE.DirectionalLight(0xc2cfe6, 0.85);
    key.position.set(10, 12, 7);
    key.castShadow = this.mode === 'full'; // sem sombras no modo paisagem
    key.shadow.mapSize.set(4096, 4096);
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 40;
    key.shadow.camera.left = -12;
    key.shadow.camera.right = 12;
    key.shadow.camera.top = 12;
    key.shadow.camera.bottom = -12;
    key.shadow.bias = -0.0004;
    key.shadow.normalBias = 0.02;
    key.shadow.radius = 1.6;
    this.scene.add(key);

    // Luz de contorno (rim) azulada: realça o brilho gelado nas bordas boleadas
    const rim = new THREE.DirectionalLight(0xe6f4ff, 0.5);
    rim.position.set(-8, 7, -8);
    this.scene.add(rim);

    // Chão de neve: MESMA textura dos blocos (cor + normal + roughness, sem diffuse)
    const gLoader = new THREE.TextureLoader();
    const gbase = 'assets/textures/snow/snow_02_';
    const groundNormal = gLoader.load(gbase + 'nor_gl_2k.jpg');
    const groundRough = gLoader.load(gbase + 'rough_2k.jpg');
    for (const t of [groundNormal, groundRough]) {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(60, 60);
      t.anisotropy = 8;
    }
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x7b818a,
      normalMap: groundNormal,
      normalScale: new THREE.Vector2(1.3, 1.3),
      roughnessMap: groundRough,
      roughness: 0.9,
      metalness: 0
    });
    // Terreno com relevo real (dunas de neve), plano no centro p/ o iglu assentar
    const gGeo = new THREE.PlaneGeometry(170, 170, 220, 220);
    const gp = gGeo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < gp.count; i++) {
      const x = gp.getX(i), y = gp.getY(i);
      const dist = Math.sqrt(x * x + y * y);
      // paisagem: terreno uniforme (sem bacia central que vira "sombra"); full: plano no centro p/ o iglu
      const falloff = this.mode === 'landscape' ? 1 : THREE.MathUtils.smoothstep(dist, 6, 20);
      const big = (this.noise3(x * 0.1, 0, y * 0.1) - 0.5) * 1.1;      // ondulação baixa
      const mid = (this.noise3(x * 0.3, 7, y * 0.3) - 0.5) * 0.5;      // ondulações
      const small = (this.noise3(x * 0.85, 13, y * 0.85) - 0.5) * 0.25; // relevo fino
      gp.setZ(i, (big + mid + small) * falloff);
    }
    gp.needsUpdate = true;
    gGeo.computeVertexNormals();
    const ground = new THREE.Mesh(gGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    ground.receiveShadow = true;
    this.scene.add(ground);

    this.igloo = new THREE.Group();
    this.igloo.position.y = 0.1; // assenta o iglu um pouco mais para baixo (afunda na neve)
    this.scene.add(this.igloo);

    // Luz branca no centro do iglu: acende no hover e retroilumina os blocos (contraluz)
    this.backLight = new THREE.PointLight(0xffffff, 0, 18, 1.4);
    this.backLight.position.set(0, 2.6, 0);
    this.scene.add(this.backLight);

    this.buildBackdrop();
    this.buildAurora();
    this.buildStars();
    this.buildSnow();

    // Eventos de mouse no canvas
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerleave', this.onPointerLeave);
  }

  // ---------- Cenário ao fundo (montanhas de neve) ----------
  /** Montanha de neve arredondada: esfera deformada por ruído (encostas naturais). */
  private mountainGeo(seed: number): THREE.BufferGeometry {
    const geo = new THREE.SphereGeometry(1, 140, 96);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      const n1 = this.noise3(v.x * 1.4 + seed, v.y * 1.4 + seed, v.z * 1.4 + seed) - 0.5;
      const n2 = this.noise3(v.x * 3.0 + seed * 1.7, v.y * 3.0 + seed * 1.7, v.z * 3.0 + seed * 1.7) - 0.5;
      const n3 = this.noise3(v.x * 6.5 + seed * 2.3, v.y * 6.5 + seed * 2.3, v.z * 6.5 + seed * 2.3) - 0.5;
      const n4 = this.noise3(v.x * 13 + seed * 3.1, v.y * 13 + seed * 3.1, v.z * 13 + seed * 3.1) - 0.5;
      v.multiplyScalar(1 + n1 * 0.34 + n2 * 0.2 + n3 * 0.12 + n4 * 0.06);
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
    const uv = geo.attributes.uv as THREE.BufferAttribute;
    geo.setAttribute('uv2', new THREE.BufferAttribute(uv.array, 2));
    return geo;
  }

  private buildBackdrop(): void {
    const backdrop = new THREE.Group();

    // Textura PBR de neve nas montanhas (instâncias próprias, bem repetidas)
    const loader = new THREE.TextureLoader();
    const mNormal = loader.load('assets/textures/snow/snow_02_nor_gl_2k.jpg');
    const mRough = loader.load('assets/textures/snow/snow_02_rough_2k.jpg');
    const mAO = loader.load('assets/textures/snow/snow_02_ao_2k.jpg');
    for (const t of [mNormal, mRough, mAO]) {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(9, 9);
      t.anisotropy = 8;
    }
    const mat = new THREE.MeshStandardMaterial({
      color: 0x7b818a,
      normalMap: mNormal,
      normalScale: new THREE.Vector2(2.0, 2.0),
      roughnessMap: mRough,
      roughness: 0.92,
      aoMap: mAO,
      aoMapIntensity: 1.0,
      metalness: 0
    });

    // Apenas duas montanhas arredondadas no fundo: uma grande e uma pequena
    const mountains = [
      { x: -34, z: -140, r: 95, sy: 1.15 }, // grande (ao fundo, domina o horizonte) — mais alta
      { x: 40, z: -106, r: 54, sy: 0.62 },  // pequena (à frente, sobrepondo a lateral)
      // montanhas extras do lado direito
      { x: 95, z: -150, r: 90, sy: 1.1 },   // grande, bem ao fundo à direita — mais alta
      { x: 70, z: -120, r: 60, sy: 0.7 },   // média, direita
      { x: 120, z: -125, r: 55, sy: 0.65 }, // direita, mais à frente
      // direita mais próximas (preenchem o vazio com a câmera baixa)
      { x: 52, z: -68, r: 34, sy: 0.72 },
      { x: 88, z: -88, r: 50, sy: 0.8 },
      { x: 130, z: -98, r: 58, sy: 0.78 }
    ];
    for (let i = 0; i < mountains.length; i++) {
      const m = mountains[i];
      const pm = mat.clone();
      pm.color = new THREE.Color(0x7b818a).multiplyScalar(0.82 + Math.random() * 0.16);
      const mesh = new THREE.Mesh(this.mountainGeo(i * 5.1 + 11), pm);
      mesh.scale.set(m.r, m.r * m.sy, m.r);
      mesh.position.set(m.x, -m.r * m.sy * 0.5, m.z); // meio enterrada (só o topo arredondado)
      backdrop.add(mesh);
    }

    this.scene.add(backdrop);
  }

  /**
   * Aurora boreal numa CÚPULA (esfera vista por dentro) que envolve a cena — assim o céu
   * fica coberto em qualquer ângulo da câmera (um plano chato falha visto de lado).
   * A fita é uma faixa em elevação (altura no céu), ondulada ao longo do azimute.
   */
  private buildAurora(): void {
    const geo = new THREE.SphereGeometry(300, 64, 40);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 } },
      transparent: true,
      blending: THREE.AdditiveBlending, // soma luz -> brilho sobre o céu
      depthWrite: false,
      side: THREE.BackSide,             // renderiza por dentro (cúpula)
      fog: false,
      vertexShader: `
        varying vec3 vDir;
        void main() {
          vDir = normalize(position); // direção no céu (centro da esfera = origem)
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vDir;
        uniform float uTime;

        float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
        float noise(vec2 p){
          vec2 i = floor(p); vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
                     mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
        }
        float fbm(vec2 p){
          float v = 0.0, a = 0.5;
          for (int i = 0; i < 5; i++) { v += a * noise(p); p *= 2.0; a *= 0.5; }
          return v;
        }

        void main() {
          vec3 dir = normalize(vDir);
          float t = uTime * 0.05;

          float elev = dir.y;                 // 0 = horizonte, 1 = zênite
          float azi = atan(dir.z, dir.x);     // -pi..pi (volta ao redor)
          vec2 rc = vec2(cos(azi), sin(azi));  // coords contínuas (sem emenda no azimute)

          float horizon = smoothstep(0.02, 0.16, elev); // some perto do horizonte

          float aSum = 0.0;
          vec3 colSum = vec3(0.0);

          // Duas cortinas sobrepostas em alturas/fases diferentes => profundidade realista
          for (int i = 0; i < 2; i++) {
            float fi = float(i);
            float base = 0.34 + fi * 0.10;
            float wave = base
              + 0.10 * sin(azi * 2.0 + t * 1.0 + fi * 1.7)
              + 0.06 * sin(azi * 4.0 - t * 0.8 + fi * 2.3)
              + 0.04 * sin(azi * 7.0 + t * 0.5);
            float d = elev - wave;

            // Filamentos verticais finos (ridged), subindo e ondulando
            float warp = fbm(rc * 3.0 + t * 0.1 + fi * 5.0);
            float ray = fbm(rc * (10.0 + fi * 4.0) + vec2(warp * 2.0, elev * 6.0 - t * 0.7));
            ray = 1.0 - abs(ray * 2.0 - 1.0);
            ray = pow(ray, 3.0);

            // Brilho suave (corpo) + filamentos brilhantes
            float glow = exp(-d * d * 22.0);
            float fila = exp(-d * d * 70.0) * ray;
            float inten = (glow * 0.35 + fila * 1.0) * horizon;

            // Cor por altura: verde no corpo, violeta no topo, magenta/rosa embaixo
            vec3 c = vec3(0.10, 1.0, 0.42);
            c = mix(c, vec3(0.55, 0.20, 0.85), smoothstep(0.0, 0.18, d));   // violeta no topo
            c = mix(c, vec3(0.95, 0.22, 0.55), smoothstep(0.0, 0.16, -d));  // magenta embaixo

            aSum += inten;
            colSum += c * inten;
          }

          float a = clamp(aSum, 0.0, 1.0) * 0.95;
          vec3 col = colSum / max(aSum, 0.001); // cor média ponderada pela intensidade

          gl_FragColor = vec4(col, a);
        }
      `
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(0, 0, 0); // centrada na origem; a câmera fica sempre dentro da cúpula
    this.scene.add(mesh);
    this.auroraMat = mat;
  }

  // ---------- Neve caindo (poucos flocos, partículas suaves) ----------
  /** Sprite circular com degradê (floco macio, não quadrado). */
  private snowSprite(): THREE.CanvasTexture {
    const s = 64;
    const c = document.createElement('canvas');
    c.width = c.height = s;
    const ctx = c.getContext('2d')!;
    const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.35, 'rgba(255,255,255,0.85)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, s, s);
    return new THREE.CanvasTexture(c);
  }

  private buildSnow(): void {
    const sprite = this.snowSprite();
    const group = new THREE.Group();

    // Duas camadas: perto (poucos, grandes) e fundo (mais, pequenos) = profundidade
    const layers = [
      { count: 110, size: 0.34, opacity: 0.9 },
      { count: 230, size: 0.16, opacity: 0.7 }
    ];
    for (const l of layers) {
      const pos = new Float32Array(l.count * 3);
      for (let i = 0; i < l.count; i++) {
        pos[i * 3] = (Math.random() - 0.5) * 64;
        pos[i * 3 + 1] = Math.random() * 34;
        pos[i * 3 + 2] = (Math.random() - 0.5) * 50 - 6;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      const mat = new THREE.PointsMaterial({
        map: sprite,
        color: 0xf4f7fa,
        size: l.size,
        transparent: true,
        opacity: l.opacity,
        depthWrite: false,
        sizeAttenuation: true,
        alphaTest: 0.01,
        fog: true
      });
      group.add(new THREE.Points(geo, mat));
    }
    this.snow = group;
    this.scene.add(group);
  }

  /** Estrelas realistas no céu (hemisfério superior), atrás da aurora e das montanhas. */
  private buildStars(): void {
    const count = 900;
    const R = 295; // raio da cúpula de estrelas (dentro do far plane = 380)
    const pos = new Float32Array(count * 3);
    const phase = new Float32Array(count);
    const size = new Float32Array(count);
    const bright = new Float32Array(count);
    const color = new Float32Array(count * 3);

    // Paleta por temperatura de cor (do azul quente ao alaranjado frio), pesada p/ branco.
    const palette: [number, number, number][] = [
      [0.74, 0.82, 1.00], // azul-branca (estrela quente)
      [0.86, 0.91, 1.00], // branco-azulada
      [1.00, 1.00, 1.00], // branca
      [1.00, 1.00, 1.00], // branca (mais comum)
      [1.00, 0.96, 0.88], // branco-quente
      [1.00, 0.88, 0.70], // amarelada
      [1.00, 0.78, 0.58]  // alaranjada (estrela fria)
    ];

    for (let i = 0; i < count; i++) {
      // direção aleatória no hemisfério superior (componente vertical de 0.04 a 1)
      const y = 0.04 + Math.random() * 0.96;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const ang = Math.random() * Math.PI * 2;
      pos[i * 3] = Math.cos(ang) * r * R;
      pos[i * 3 + 1] = y * R;
      pos[i * 3 + 2] = Math.sin(ang) * r * R;

      phase[i] = Math.random() * Math.PI * 2;                  // fase da cintilação
      // magnitude: muitas fracas, poucas brilhantes (^3 enviesa p/ baixo)
      const mag = Math.pow(Math.random(), 3);
      size[i] = 0.6 + mag * 3.0;                               // estrelas fortes ficam maiores
      bright[i] = 0.35 + mag * 0.65;                           // ...e mais intensas

      const c = palette[Math.floor(Math.random() * palette.length)];
      color[i * 3] = c[0];
      color[i * 3 + 1] = c[1];
      color[i * 3 + 2] = c[2];
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phase, 1));
    geo.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
    geo.setAttribute('aBright', new THREE.BufferAttribute(bright, 1));
    geo.setAttribute('aColor', new THREE.BufferAttribute(color, 3));

    this.starMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      fog: false,
      uniforms: { uTime: { value: 0 }, uSize: { value: 5.0 } },
      vertexShader: `
        attribute float aPhase;
        attribute float aSize;
        attribute float aBright;
        attribute vec3 aColor;
        uniform float uTime;
        uniform float uSize;
        varying float vTw;
        varying float vBright;
        varying float vBig;
        varying float vAng;
        varying vec3 vColor;
        void main() {
          // cintilação sutil: amplitude menor e velocidade própria de cada estrela
          float sp = 1.4 + aPhase * 0.35;
          vTw = 0.78 + 0.22 * sin(uTime * sp + aPhase);
          vBright = aBright;
          vBig = smoothstep(1.2, 3.4, aSize); // pontas mais longas nas estrelas maiores
          vAng = aPhase;                       // gira o sparkle p/ não ficarem todos iguais
          vColor = aColor;
          gl_PointSize = uSize * aSize;        // tamanho fixo (sem atenuação)
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying float vTw;
        varying float vBright;
        varying float vBig;
        varying float vAng;
        varying vec3 vColor;
        void main() {
          vec2 p = gl_PointCoord - vec2(0.5);
          // gira o quadrado do ponto p/ variar a orientação do brilho entre estrelas
          float ca = cos(vAng), sa = sin(vAng);
          p = mat2(ca, -sa, sa, ca) * p;
          float d = length(p);
          if (d > 0.5) discard;

          // núcleo brilhante (centro intenso, queda exponencial)
          float core = exp(-d * 16.0);

          // sparkle de 4 pontas: cruz fina que afina em direção às extremidades
          vec2 ap = abs(p);
          float v = max(ap.x, ap.y);
          float u = min(ap.x, ap.y);
          float spike = (1.0 - smoothstep(0.0, 0.05 + 0.13 * v, u))  // fica fino longe do eixo
                      * (1.0 - smoothstep(0.0, 0.5, v));             // e some na ponta
          // pontas crescem com o brilho: fracas viram pontinhos, fortes viram estrelas
          float a = core * 0.95 + spike * 0.85 * mix(0.15, 1.0, vBig);

          gl_FragColor = vec4(vColor, a * vTw * vBright);
        }
      `
    });

    const points = new THREE.Points(geo, this.starMat);
    points.renderOrder = -1; // bem ao fundo (céu)
    this.scene.add(points);
  }

  // ---------- Texturas de neve PBR (Poly Haven, CC0) ----------
  private loadSnowTextures(): void {
    const loader = new THREE.TextureLoader();
    const base = 'assets/textures/snow/snow_02_';
    this.snowMap = loader.load(base + 'diff_2k.jpg');
    this.snowNormal = loader.load(base + 'nor_gl_2k.jpg');
    this.snowRough = loader.load(base + 'rough_2k.jpg');
    this.snowAO = loader.load(base + 'ao_2k.jpg');

    this.snowMap.encoding = THREE.sRGBEncoding;
    for (const t of [this.snowMap, this.snowNormal, this.snowRough, this.snowAO]) {
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(2.6, 2.6); // grão fino de neve (não relevo rochoso)
      t.anisotropy = 16;
    }
  }

  // ---------- Geometria do iglu ----------
  private iceMaterial(): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
      color: 0x868a8e,          // neve fosca bem acinzentada (cinza neutro)
      normalMap: this.snowNormal,
      normalScale: new THREE.Vector2(0.7, 0.7), // textura sutil (não liso/brilhante)
      roughnessMap: this.snowRough,
      roughness: 0.97,          // bem fosco, aspecto de pó de neve
      aoMap: this.snowAO,
      aoMapIntensity: 0.6,
      metalness: 0,
      emissive: 0x8fb0d2,       // brilho gelado sutil (realça no hover)
      emissiveIntensity: 0.04
    });
    // Variação de tom entre os blocos (uns mais "empoeirados"/escuros)
    const tint = 0.82 + Math.random() * 0.16;
    mat.color.multiplyScalar(tint);

    // Brilho gelado nas bordas (Fresnel) — sutil, só insinua o gelo nas arestas
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uGlowColor = { value: new THREE.Color(0xc8dcec) };
      shader.uniforms.uGlowStrength = { value: 0.45 };
      shader.fragmentShader =
        'uniform vec3 uGlowColor;\nuniform float uGlowStrength;\n' + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
         float _fres = pow(1.0 - abs(dot(normalize(normal), normalize(vViewPosition))), 3.0);
         totalEmissiveRadiance += uGlowColor * _fres * uGlowStrength;`
      );
    };
    return mat;
  }

  private makeBlock(w: number, h: number, d: number): THREE.Mesh {
    // Arredondamento variado por bloco: uns mais quadrados, outros bem boleados
    const round = 0.14 + Math.random() * 0.34; // 0.14 .. 0.48
    const geo = this.roundedBox(w, h, d, round);
    const mesh = new THREE.Mesh(geo, this.iceMaterial());
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  // Hash inteiro 32-bit -> 0..1 (para o ruído 3D)
  private hash3(x: number, y: number, z: number): number {
    let n = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(z, 1274126177);
    n = Math.imul(n ^ (n >>> 13), 1274126177);
    return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
  }

  // Ruído de valor 3D com interpolação suave (trilinear + smoothstep)
  private noise3(x: number, y: number, z: number): number {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
    const c = (i: number, j: number, k: number) => this.hash3(xi + i, yi + j, zi + k);
    const lp = (a: number, b: number, t: number) => a + (b - a) * t;
    const x00 = lp(c(0, 0, 0), c(1, 0, 0), u);
    const x10 = lp(c(0, 1, 0), c(1, 1, 0), u);
    const x01 = lp(c(0, 0, 1), c(1, 0, 1), u);
    const x11 = lp(c(0, 1, 1), c(1, 1, 1), u);
    return lp(lp(x00, x10, v), lp(x01, x11, v), w);
  }

  /** Caixa arredondada e DESLOCADA por ruído 3D (relevo real de neve moldada). */
  private roundedBox(w: number, h: number, d: number, round: number): THREE.BufferGeometry {
    const geo = new THREE.BoxGeometry(w, h, d, 14, 14, 14);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const hx = w / 2, hy = h / 2, hz = d / 2;
    const v = new THREE.Vector3();

    // 1) Arredonda cantos/arestas
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      const u = v.x / hx, a = v.y / hy, b = v.z / hz;
      const len = Math.sqrt(u * u + a * a + b * b) || 1;
      const nu = u * (1 - round) + (u / len) * round;
      const na = a * (1 - round) + (a / len) * round;
      const nb = b * (1 - round) + (b / len) * round;
      pos.setXYZ(i, nu * hx, na * hy, nb * hz);
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();

    // 2) Desloca ao longo da normal com ruído 3D (lombadas + grão fino)
    const nrm = geo.attributes.normal as THREE.BufferAttribute;
    const seed = Math.random() * 50;
    const nv = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      nv.fromBufferAttribute(nrm, i);
      const lump = this.noise3(v.x * 1.8 + seed, v.y * 1.8 + seed, v.z * 1.8 + seed) - 0.5;
      const grain = this.noise3(v.x * 4.5 + seed * 2, v.y * 4.5 + seed * 2, v.z * 4.5 + seed * 2) - 0.5;
      const fine = this.noise3(v.x * 9.0 + seed * 3, v.y * 9.0 + seed * 3, v.z * 9.0 + seed * 3) - 0.5;
      const disp = lump * 0.025 + grain * 0.025 + fine * 0.018;
      pos.setXYZ(i, v.x + nv.x * disp, v.y + nv.y * disp, v.z + nv.z * disp);
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();

    // uv2 = uv (necessário para o aoMap)
    const uv = geo.attributes.uv as THREE.BufferAttribute;
    geo.setAttribute('uv2', new THREE.BufferAttribute(uv.array, 2));
    return geo;
  }

  private addBlock(mesh: THREE.Mesh, pos: THREE.Vector3, lookAt: THREE.Vector3): void {
    mesh.position.copy(pos);
    mesh.lookAt(lookAt); // +Z aponta pra fora, seguindo a curvatura
    const data: BlockData = {
      base: pos.clone(),
      normal: pos.clone().sub(lookAt).normalize(),
      phase: this.t + this.blocks.length * 0.6 + (this.blocks.length % 5) * 0.25,
      hover: false,
      offset: 0,
      scaleV: 1
    };
    mesh.userData = data;
    this.igloo.add(mesh);
    this.blocks.push(mesh);
  }

  /**
   * Tenta carregar um modelo 3D real (assets/models/igloo.glb). Se existir,
   * substitui os blocos procedurais por ele; senão, mantém o iglu procedural.
   */
  private tryLoadModel(): void {
    const loader = new GLTFLoader();
    loader.load(
      'assets/models/igloo1.glb',
      (gltf) => {
        // remove o iglu procedural (blocos + casca interna)
        this.blocks = [];
        while (this.igloo.children.length) {
          this.igloo.remove(this.igloo.children[0]);
        }

        const model = gltf.scene;
        model.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh) {
            m.castShadow = true;
            m.receiveShadow = true;
            // Só aplica a nossa textura de neve se o modelo vier SEM textura própria.
            const mat = m.material as THREE.MeshStandardMaterial;
            const hasTexture = !Array.isArray(m.material) && !!(mat && mat.map);
            if (!hasTexture) {
              const g = m.geometry as THREE.BufferGeometry;
              const uv = g.attributes.uv as THREE.BufferAttribute | undefined;
              if (uv && !g.attributes.uv2) {
                g.setAttribute('uv2', new THREE.BufferAttribute(uv.array, 2));
              }
              m.material = this.iceMaterial();
            }
          }
        });

        model.rotation.y = Math.PI; // igloo3 vem de costas: vira a entrada p/ frente

        // Auto-ajuste: escala para ~5 de altura e assenta a base no chão, centrado
        const box = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        box.getSize(size);
        const s = 5 / (size.y || 1);
        model.scale.setScalar(s);

        const box2 = new THREE.Box3().setFromObject(model);
        const c = new THREE.Vector3();
        box2.getCenter(c);
        model.position.x -= c.x;
        model.position.z -= c.z;
        model.position.y -= box2.min.y + 0.2; // base levemente fincada na neve

        this.igloo.add(model);

        // Destaca os blocos do modelo (meshes "Object001...") p/ flutuarem individualmente
        this.scene.updateMatrixWorld(true);
        const floatGroup = new THREE.Group();
        this.scene.add(floatGroup);
        const hitGroup = new THREE.Group();
        this.scene.add(hitGroup);
        // material invisível (não desenha, mas continua detectável pelo raycaster)
        const hitMat = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false });

        const blockMeshes: THREE.Mesh[] = [];
        model.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh && /Object001|Igloo_Snow|Igloo_Ice/i.test(m.name)) { blockMeshes.push(m); }
        });
        for (const m of blockMeshes) {
          floatGroup.attach(m); // reparenta preservando o transform mundial
          const out = new THREE.Vector3(m.position.x, 0, m.position.z);
          if (out.lengthSq() < 0.04) { out.set(0, 1, 0); } else { out.normalize(); }
          const d: BlockData = {
            base: m.position.clone(),
            normal: out,                       // hover empurra o bloco p/ fora
            phase: Math.random() * Math.PI * 2,
            hover: false,
            offset: 0,
            scaleV: 1,
            baseScale: m.scale.clone()         // preserva o tamanho real do bloco
          };
          m.userData = d;
          this.floatBlocks.push(m);

          // Colisor fixo na posição original do bloco (não sobe no hover)
          const proxy = new THREE.Mesh(m.geometry, hitMat);
          proxy.position.copy(m.position);
          proxy.quaternion.copy(m.quaternion);
          proxy.scale.copy(m.scale);
          proxy.userData = { target: m };
          hitGroup.add(proxy);
          this.hitProxies.push(proxy);
        }
      },
      undefined,
      () => { /* sem modelo: mantém o iglu procedural como fallback */ }
    );
  }

  /** Carrega o boneco de neve e posiciona na frente do iglu, na neve. */
  private loadPenguin(): void {
    const loader = new GLTFLoader();
    loader.load(
      'assets/models/penguin.glb',
      (gltf) => {
        const m = gltf.scene;
        m.traverse((o) => {
          const me = o as THREE.Mesh;
          if (me.isMesh) { me.castShadow = true; me.receiveShadow = true; }
        });

        // escala para ~2.6 de altura (menor que o iglu)
        const box = new THREE.Box3().setFromObject(m);
        const size = new THREE.Vector3();
        box.getSize(size);
        m.scale.setScalar(2.6 / (size.y || 1));
        m.rotation.y = Math.PI * 1.5; // +180° novamente (volta à orientação original)

        // posiciona na frente do iglu (+Z), levemente para o lado, base na neve
        const box2 = new THREE.Box3().setFromObject(m);
        const c = new THREE.Vector3();
        box2.getCenter(c);
        m.position.x += 0.2 - c.x;
        m.position.z += 8.5 - c.z;
        m.position.y -= box2.min.y;

        this.scene.add(m);
        this.idlePenguins.push({ obj: m, baseY: m.position.y, baseScale: m.scale.x, phase: 0 });
      },
      undefined,
      () => { /* sem boneco: ignora */ }
    );
  }

  private loadPenguin2(): void {
    const loader = new GLTFLoader();
    loader.load(
      'assets/models/penguin2.glb',
      (gltf) => {
        const m = gltf.scene;
        m.traverse((o) => {
          const me = o as THREE.Mesh;
          if (me.isMesh) { me.castShadow = true; me.receiveShadow = true; }
        });

        // escala para ~2.6 de altura (menor que o iglu)
        const box = new THREE.Box3().setFromObject(m);
        const size = new THREE.Vector3();
        box.getSize(size);
        m.scale.setScalar(2.6 / (size.y || 1));
        m.rotation.y = Math.PI * 2.6; // +180° novamente (volta à orientação original)

        // posiciona na frente do iglu (+Z), levemente para o lado, base na neve
        const box2 = new THREE.Box3().setFromObject(m);
        const c = new THREE.Vector3();
        box2.getCenter(c);
        m.position.x += -7 - c.x;
        m.position.z += -0.9 - c.z;
        m.position.y -= box2.min.y;

        this.scene.add(m);
        this.idlePenguins.push({ obj: m, baseY: m.position.y, baseScale: m.scale.x, phase: 1.7 });
      },
      undefined,
      () => { /* sem boneco: ignora */ }
    );
  }

  /** Pinguim que desliza no gelo, na frente do iglu (visível no início do hero). */
  private loadSlidePenguin(): void {
    const loader = new GLTFLoader();
    loader.load(
      'assets/models/penguin_swimming.glb',
      (gltf) => {
        const m = gltf.scene;
        m.traverse((o) => {
          const me = o as THREE.Mesh;
          if (me.isMesh) { me.castShadow = true; me.receiveShadow = true; }
        });

        // escala ~1.2 de altura (menor)
        const box = new THREE.Box3().setFromObject(m);
        const size = new THREE.Vector3();
        box.getSize(size);
        m.scale.setScalar(1.2 / (size.y || 1));
        m.rotation.y = Math.PI; // aponta na direção do deslize (-X)

        // assenta na neve, ATRÁS do iglu (-Z); o X é animado no loop (passa por trás do domo)
        const box2 = new THREE.Box3().setFromObject(m);
        const c = new THREE.Vector3();
        box2.getCenter(c);
        m.position.z += -14 - c.z;
        this.slideBaseY = -box2.min.y - 0.15; // afunda um pouquinho na neve
        m.position.y = this.slideBaseY;

        // Animação embutida (asas batendo + corpo acompanhando)
        if (gltf.animations && gltf.animations.length) {
          this.slideMixer = new THREE.AnimationMixer(m);
          const clip = THREE.AnimationClip.findByName(gltf.animations, 'Swim') || gltf.animations[0];
          this.slideMixer.clipAction(clip).play();
        }

        this.slideStart = this.t; // começa a contar o deslize a partir de agora
        this.slidePenguin = m;
        this.scene.add(m);
      },
      undefined,
      () => { /* sem modelo: ignora */ }
    );
  }

  private buildIgloo(): void {
    const R = 4.2;
    const rings = 6;
    const center = new THREE.Vector3(0, 0, 0);
    const elevStep = (1 / rings) * (Math.PI / 2) * 0.92;
    const blockH = R * elevStep * 1.08;   // compensa o encolhimento do arredondamento variado
    const seam = 1.06;                     // blocos encostam; a casca cobre as frestas finas

    // Domo: anéis de latitude (blocos encostam, sem sobrepor)
    for (let k = 0; k < rings; k++) {
      const elev = k * elevStep + elevStep / 2; // centro de cada fileira
      const ringY = R * Math.sin(elev);
      const ringR = R * Math.cos(elev);
      const count = Math.max(6, Math.round((2 * Math.PI * ringR) / 1.5));
      const arc = (2 * Math.PI * ringR) / count;
      const offset = (k % 2) * (Math.PI / count); // alterna meia fileira (alvenaria)

      for (let i = 0; i < count; i++) {
        const theta = (i / count) * Math.PI * 2 + offset;
        // Abertura da entrada: frente em +Z (theta = PI/2)
        const dFront = Math.abs(Math.atan2(Math.sin(theta - Math.PI / 2), Math.cos(theta - Math.PI / 2)));
        if (k <= 1 && dFront < 0.55) { continue; }

        const x = ringR * Math.cos(theta);
        const z = ringR * Math.sin(theta);
        // tamanhos quase uniformes (variação mínima, sem abrir frestas)
        const rw = 0.99 + Math.random() * 0.03;
        const rh = 0.99 + Math.random() * 0.03;
        const block = this.makeBlock(arc * seam * rw, blockH * rh, 0.7 + Math.random() * 0.05);
        this.addBlock(block, new THREE.Vector3(x, ringY, z), center);
      }
    }

    // Bloco de cobertura no topo (fecha a abóbada)
    const topY = R * Math.sin(rings * elevStep);
    const cap = this.makeBlock(1.3, 1.3, 1.3);
    this.addBlock(cap, new THREE.Vector3(0, topY - 0.5, 0), center);

    // Túnel de entrada (arco em +Z)
    const archR = 1.55;
    const archCount = 8;
    const archStep = (Math.PI * archR) / (archCount - 1);
    const tunnelDepth = 0.85;
    for (let j = 0; j < 2; j++) {
      const z = 3.7 + j * tunnelDepth;
      for (let a = 0; a < archCount; a++) {
        const ang = -0.1 + (a / (archCount - 1)) * (Math.PI + 0.2);
        const x = archR * Math.cos(ang);
        const y = archR * Math.sin(ang);
        const arcW = archStep * seam;
        const block = this.makeBlock(arcW, 0.55, tunnelDepth * seam);
        block.position.set(x, y, z);
        block.rotation.z = ang + Math.PI / 2; // alinha o bloco tangente ao arco
        const data: BlockData = {
          base: block.position.clone(),
          normal: new THREE.Vector3(Math.cos(ang), Math.sin(ang), 0),
          phase: this.blocks.length * 0.5,
          hover: false, offset: 0, scaleV: 1
        };
        block.userData = data;
        this.igloo.add(block);
        this.blocks.push(block);
      }
    }
  }

  // ---------- Interação ----------
  private onPointerMove = (e: PointerEvent): void => {
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.pointer.set(nx, ny);
    this.mouseX = nx;
    this.mouseY = ny;
  };

  private onPointerLeave = (): void => {
    this.pointer.set(-10, -10);
    this.mouseX = 0;
    this.mouseY = 0;
  };

  // ---------- Loop ----------
  private animate = (): void => {
    this.frameId = requestAnimationFrame(this.animate);
    const now = performance.now();
    const dt = Math.min((now - this.lastTime) / 1000, 0.05);
    this.lastTime = now;
    this.t += dt;

    // Aurora boreal: anima as cortinas de luz
    if (this.auroraMat) { this.auroraMat.uniforms['uTime'].value = this.t; }
    if (this.starMat) { this.starMat.uniforms['uTime'].value = this.t; }

    // Hover: raycast nos blocos procedurais + colisores fixos do modelo
    const targets = this.blocks.concat(this.hitProxies);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(targets, false);

    // Centro do hover = ponto que o cursor toca, SUAVIZADO (a superfície irregular
    // faz o ponto tremer; suavizar evita que os blocos da borda fiquem piscando).
    if (hits.length) {
      if (this.hoverActive) { this.hoverPt.lerp(hits[0].point, 0.3); }
      else { this.hoverPt.copy(hits[0].point); }
      this.hoverActive = true;
    } else {
      this.hoverActive = false;
    }
    const HOVER_RADIUS = 2.6; // raio (mundo) — pega vários blocos por vez
    const tmpWorld = new THREE.Vector3();
    // testa se a posição de repouso (base, em mundo) do bloco está dentro do raio do cursor.
    // Histerese: quem já está levitando só "solta" num raio maior (não tremula na beirada).
    const isHovered = (block: THREE.Mesh, base: THREE.Vector3, offset: number): boolean => {
      if (!this.hoverActive) { return false; }
      tmpWorld.copy(base);
      block.parent!.localToWorld(tmpWorld);
      const r = offset > 0.1 ? HOVER_RADIUS + 0.7 : HOVER_RADIUS;
      return tmpWorld.distanceTo(this.hoverPt) < r;
    };
    const wEuler = new THREE.Euler();
    const wQuat = new THREE.Quaternion();

    // Luz interna do iglu: acende suavemente quando o cursor está sobre o iglu
    if (this.backLight) {
      const backTarget = this.hoverActive ? 6 : 0;
      this.backLight.intensity += (backTarget - this.backLight.intensity) * 0.08;
    }

    for (const block of this.blocks) {
      const d = block.userData as BlockData;
      d.hover = isHovered(block, d.base, d.offset);
      d.offset += ((d.hover ? 1.7 : 0) - d.offset) * 0.045; // sobe mais alto e mais devagar
      d.scaleV += ((d.hover ? 1.12 : 1) - d.scaleV) * 0.045;

      // Parado na base; no hover levanta e FLUTUA bem amplo e lento (gravidade zero)
      block.position.copy(d.base).addScaledVector(d.normal, d.offset);
      const hf = d.offset / 1.7; // 0 = parado, 1 = totalmente levantado
      block.position.x += Math.sin(this.t * 0.5 + d.phase * 1.3) * 0.16 * hf;
      block.position.y += Math.sin(this.t * 0.7 + d.phase) * 0.22 * hf;
      block.position.z += Math.cos(this.t * 0.6 + d.phase * 0.7) * 0.16 * hf;
      block.scale.setScalar(d.scaleV);

      // Tombamento lento enquanto levita (volta à orientação base ao descer)
      if (!d.baseQuat) { d.baseQuat = block.quaternion.clone(); }
      wEuler.set(
        Math.sin(this.t * 0.5 + d.phase) * 0.5 * hf,
        Math.cos(this.t * 0.4 + d.phase * 1.3) * 0.6 * hf,
        Math.sin(this.t * 0.45 + d.phase * 0.7) * 0.5 * hf
      );
      block.quaternion.copy(d.baseQuat).multiply(wQuat.setFromEuler(wEuler));

      // Só o brilho gelado sutil (a iluminação vem da luz interna, não do emissivo)
      const mat = block.material as THREE.MeshStandardMaterial;
      mat.emissiveIntensity = 0.06 + hf * 0.18;
    }

    // Blocos do iglu: PARADOS na posição base; ao passar o mouse, o bloco LEVANTA (sobe no Y)
    for (const block of this.floatBlocks) {
      const d = block.userData as BlockData;
      d.hover = isHovered(block, d.base, d.offset);
      d.offset += ((d.hover ? 2.2 : 0) - d.offset) * 0.045;  // sobe mais alto e mais devagar
      d.scaleV += ((d.hover ? 1.08 : 1) - d.scaleV) * 0.045;

      // Parado na base; no hover levanta e FLUTUA bem amplo e lento (gravidade zero)
      const hf = d.offset / 2.2; // 0 = parado, 1 = totalmente levantado
      block.position.copy(d.base);
      block.position.x += Math.sin(this.t * 0.45 + d.phase * 1.3) * 0.24 * hf;
      block.position.y += d.offset + Math.sin(this.t * 0.65 + d.phase) * 0.32 * hf;
      block.position.z += Math.cos(this.t * 0.55 + d.phase * 0.7) * 0.24 * hf;
      if (d.baseScale) { block.scale.copy(d.baseScale).multiplyScalar(d.scaleV); }

      // Tombamento lento enquanto levita (volta à orientação base ao descer)
      if (!d.baseQuat) { d.baseQuat = block.quaternion.clone(); }
      wEuler.set(
        Math.sin(this.t * 0.45 + d.phase) * 0.6 * hf,
        Math.cos(this.t * 0.35 + d.phase * 1.3) * 0.7 * hf,
        Math.sin(this.t * 0.4 + d.phase * 0.7) * 0.6 * hf
      );
      block.quaternion.copy(d.baseQuat).multiply(wQuat.setFromEuler(wEuler));

      // Brilho sutil ao levitar (a iluminação vem da luz interna, não do emissivo)
      const mm = block.material as THREE.MeshStandardMaterial;
      if (mm && !Array.isArray(block.material) && mm.emissive) {
        mm.emissiveIntensity = 0.06 + hf * 0.18;
      }
    }

    // Neve caindo com vento lateral (queda suave)
    const p = this.scrollProgress;
    const wind = 1 + p * 6;   // vento/queda aumentam: nevasca ao entrar
    for (const layer of this.snow.children) {
      const sp = (layer as THREE.Points).geometry.attributes.position as THREE.BufferAttribute;
      const arr = sp.array as Float32Array;
      for (let i = 0; i < arr.length; i += 3) {
        arr[i + 1] -= dt * (1.6 + (i % 7) * 0.18) * (1 + p * 2);    // queda
        arr[i] += (0.8 + Math.sin(this.t * 1.1 + i) * 0.5) * dt * wind; // deriva c/ vento
        if (arr[i + 1] < 0) { arr[i + 1] = 34; }                    // recicla no topo
        if (arr[i] > 32) { arr[i] -= 64; }                          // recicla na lateral
      }
      sp.needsUpdate = true;
    }

    // Pinguins fixos: respiração (pulsar de escala) + balanço lateral suave.
    for (const pg of this.idlePenguins) {
      const tt = this.t + pg.phase;
      const breath = 1 + Math.sin(tt * 1.6) * 0.02;          // peito sobe/desce (respiração)
      pg.obj.scale.set(pg.baseScale, pg.baseScale * breath, pg.baseScale);
      pg.obj.position.y = pg.baseY + Math.sin(tt * 1.6) * 0.02; // acompanha levemente
      pg.obj.rotation.z = Math.sin(tt * 0.9) * 0.03;            // balanço lateral
    }

    // Pinguim deslizando no gelo (uma única vez, lento): atravessa atrás do iglu e some.
    if (this.slidePenguin) {
      this.slideMixer?.update(dt); // bate as asas / move o corpo
      const span = 110;                                   // distância total (começa bem no canto + sai do quadro)
      const speed = 10;                                   // velocidade do deslize (maior = mais rápido)
      const xShift = 16;                                  // desloca todo o trajeto para o lado direito
      const dist = (this.t - this.slideStart) * speed;
      if (dist > span) {
        this.slidePenguin.visible = false;               // terminou a travessia: some
      } else {
        this.slidePenguin.position.x = xShift + span / 2 - dist; // trajeto deslocado p/ a direita
        this.slidePenguin.position.y = this.slideBaseY + Math.abs(Math.sin(this.t * 6)) * 0.05; // solavanco no gelo
        this.slidePenguin.rotation.z = Math.sin(this.t * 3) * 0.05; // leve gingado lateral
      }
    }

    // Tudo numa seção só: (1) câmera entra e fixa na paisagem; (2) cards passam por cima;
    // (3) pan para a direita da paisagem com o CTA final.
    if (this.mode === 'full') {
      // --- Fase 1: entrada (0 -> 0.20 do scroll). O iglu sai do quadro e as montanhas preenchem.
      const pe = Math.min(1, p / 0.20);
      const e = pe < 0.5 ? 4 * pe * pe * pe : 1 - Math.pow(-2 * pe + 2, 3) / 2; // easeInOutCubic
      // Ponto de partida afastado em telas estreitas (camFit) p/ o iglu caber inteiro;
      // ao entrar (e -> 1) converge p/ o mesmo enquadramento interno em qualquer tela.
      const f = this.camFit;
      const sX = -11 * f;
      const sY = 2.8 - 0.2 * f;
      const sZ = 12 * f;
      let camTX = sX + (0 - sX) * e;
      let camTY = sY + (11 - sY) * e;         // sobe por cima do iglu
      let camTZ = sZ + (-16 - sZ) * e;        // avança e passa do iglu
      const par = 1 - pe;                        // parallax some conforme entra
      camTX += this.mouseX * 4.5 * par;
      camTY += this.mouseY * 2.5 * par;
      let lookX = 0;
      let lookY = 2.8 + (8.5 - 2.8) * e;
      let lookZ = 0 + (-64 - 0) * e;             // olha para o fundo (montanhas)

      // --- Fase 3: pan para a DIREITA da paisagem + leve avanço (>= 0.58).
      const panStart = 0.58;
      const pp = Math.max(0, Math.min(1, (p - panStart) / (1 - panStart)));
      const ep = pp < 0.5 ? 4 * pp * pp * pp : 1 - Math.pow(-2 * pp + 2, 3) / 2; // easeInOutCubic
      camTX += (40 - 0) * ep;        // desliza bem mais para a direita
      camTZ += (-30 - -16) * ep;     // avança levemente para dentro da paisagem
      camTY += (8 - 11) * ep;        // desce um pouco
      lookX += (88 - 0) * ep;        // gira o olhar bem para a direita (montanhas da direita)
      lookY += (5 - 8.5) * ep;
      lookZ += (-80 - -64) * ep;

      // Revelação do texto durante a intro (0 = câmera no alto; 1 = pousada)
      let introReveal = 1;

      if (this.introActive) {
        // --- Intro de abertura: cai de uma vista aérea até o enquadramento atual ---
        this.introT += dt;
        const ip = Math.min(1, this.introT / this.introDur);
        const ie = ip < 0.5 ? 4 * ip * ip * ip : 1 - Math.pow(-2 * ip + 2, 3) / 2; // easeInOutCubic
        // Pose de topo (vista aérea, olhando para baixo) -> pose de repouso atual (camT*/look*)
        const topX = 0, topY = 64, topZ = 6;
        this.camera.position.set(
          topX + (camTX - topX) * ie,
          topY + (camTY - topY) * ie,
          topZ + (camTZ - topZ) * ie
        );
        this.lookSmooth.set(lookX * ie, lookY * ie, lookZ * ie); // de (0,0,0) até o olhar de repouso
        this.camera.lookAt(this.lookSmooth);
        introReveal = Math.max(0, Math.min(1, (ip - 0.55) / 0.45)); // texto surge no fim da queda
        if (ip >= 1) { this.introActive = false; }
      } else {
        // Suavização exponencial independente de FPS => navegação fluida e consistente
        const k = 1 - Math.exp(-dt * 8);
        this.camera.position.x += (camTX - this.camera.position.x) * k;
        this.camera.position.y += (camTY - this.camera.position.y) * k;
        this.camera.position.z += (camTZ - this.camera.position.z) * k;
        // suaviza também o olhar (antes aplicado direto do scroll -> causava trancos)
        this.lookSmooth.x += (lookX - this.lookSmooth.x) * k;
        this.lookSmooth.y += (lookY - this.lookSmooth.y) * k;
        this.lookSmooth.z += (lookZ - this.lookSmooth.z) * k;
        this.camera.lookAt(this.lookSmooth);
      }

      // texto inicial some na entrada (e surge ao fim da intro)
      const textFade = Math.max(0, 1 - pe / 0.6) * introReveal;
      this.contentRef.nativeElement.style.opacity = textFade.toFixed(3);
      this.scrollHintRef.nativeElement.style.opacity = textFade.toFixed(3);
      if (this.brandRef) { this.brandRef.nativeElement.style.opacity = textFade.toFixed(3); }
      this.blizzardRef.nativeElement.style.opacity = '0';

      // --- Fase 2: cards (>=0.22). Surgem, deslizam na horizontal e somem antes do pan.
      if (this.cardsRef && this.cardsTrackRef) {
        const cardsIn = Math.max(0, Math.min(1, (p - 0.22) / 0.04));
        const cardsOut = Math.max(0, Math.min(1, (p - 0.54) / 0.04)); // some antes do pan
        const cardsVis = cardsIn * (1 - cardsOut);
        const cardsEl = this.cardsRef.nativeElement;
        cardsEl.style.opacity = cardsVis.toFixed(3);
        cardsEl.style.pointerEvents = cardsVis > 0.5 ? 'auto' : 'none';
        const pc = Math.max(0, Math.min(1, (p - 0.26) / 0.26));
        const track = this.cardsTrackRef.nativeElement;
        const maxX = Math.max(0, track.scrollWidth - window.innerWidth);
        track.style.transform = `translate3d(${-(pc * maxX).toFixed(1)}px,0,0)`;
      }

      // --- CTA final: surge durante o pan para a direita.
      if (this.ctaRef) {
        const ctaFade = Math.max(0, Math.min(1, (p - 0.74) / 0.10));
        const ctaEl = this.ctaRef.nativeElement;
        ctaEl.style.opacity = ctaFade.toFixed(3);
        ctaEl.style.pointerEvents = ctaFade > 0.5 ? 'auto' : 'none';
      }
    }

    this.renderer.render(this.scene, this.camera);
  };

  // Progresso do scroll dentro do track (0 = topo, 1 = totalmente dentro)
  private onScroll = (): void => {
    const rect = this.host.nativeElement.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    this.scrollProgress = total > 0
      ? Math.max(0, Math.min(1, -rect.top / total))
      : 0;
  };

  private onResize = (): void => {
    const host = this.canvasRef.nativeElement.parentElement!;
    const w = host.clientWidth;
    const h = host.clientHeight;
    if (!w || !h) { return; }
    this.updateCamFit(w, h); // reavalia o enquadramento (rotação retrato/paisagem, resize)
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
  };
}
